import type { EditSolutionFormValues } from "../../../../../edit/form/types";
import {
  normaliseBenefitsForUpdate,
  normaliseUniqueBenefits,
  normaliseCategoryScoreForUpdate,
  normaliseSatelliteImageryForUpdate,
} from "../../../../../form/utils";
import type {
  AssessmentScorePillar,
  Pillar,
  PillarPayload,
  PillarsPayload,
  SolutionUpdatePayload,
} from "../../../../../types";
import type { AssessmentReportSectionId } from "../../../types";
import {
  assessmentScorePillars,
  getAssessmentCategoryScore,
} from "../../../../../form/components/integrity-scores/scoreCategories";

const createDataPillarUpdate = (
  pillar: Pillar | undefined,
): PillarPayload | undefined => {
  if (!pillar) return undefined;

  const scores = pillar.scores
    ? { total: pillar.scores.total ?? null }
    : undefined;
  const benefits = normaliseBenefitsForUpdate(pillar.benefits);

  if (!scores && !benefits) return undefined;

  return {
    ...(scores ? { scores } : {}),
    ...(benefits ? { benefits } : {}),
  };
};

const createDataPillarsUpdate = (
  pillars: EditSolutionFormValues["pillars"],
): PillarsPayload | undefined => {
  const carbon = createDataPillarUpdate(pillars?.carbon);
  const biodiversity = createDataPillarUpdate(pillars?.biodiversity);
  const people = createDataPillarUpdate(pillars?.people);

  if (!carbon && !biodiversity && !people) return undefined;

  return {
    ...(carbon ? { carbon } : {}),
    ...(biodiversity ? { biodiversity } : {}),
    ...(people ? { people } : {}),
  };
};

const createIntegrityPillarUpdate = (
  pillar: AssessmentScorePillar,
  values: EditSolutionFormValues,
): PillarPayload => {
  const categories = assessmentScorePillars.find(
    ({ key }) => key === pillar,
  )?.categories;

  const scores = Object.fromEntries(
    (categories ?? []).map(({ key }) => [
      key,
      normaliseCategoryScoreForUpdate(
        getAssessmentCategoryScore(values.pillars, pillar, key) ?? {},
      ),
    ]),
  ) as PillarPayload["scores"];

  return { scores };
};

const createIntegrityPillarsUpdate = (
  values: EditSolutionFormValues,
): PillarsPayload => ({
  carbon: createIntegrityPillarUpdate("carbon", values),
  biodiversity: createIntegrityPillarUpdate("biodiversity", values),
  people: createIntegrityPillarUpdate("people", values),
});

const createAssessmentDataPillarsUpdate = (
  values: EditSolutionFormValues,
): PillarsPayload => {
  const dataPillars = createDataPillarsUpdate(values.pillars);
  const integrityPillars = createIntegrityPillarsUpdate(values);
  return {
    carbon: {
      ...dataPillars?.carbon,
      scores: {
        ...dataPillars?.carbon?.scores,
        ...integrityPillars.carbon?.scores,
      },
    },
    biodiversity: {
      ...dataPillars?.biodiversity,
      scores: {
        ...dataPillars?.biodiversity?.scores,
        ...integrityPillars.biodiversity?.scores,
      },
    },
    people: {
      ...dataPillars?.people,
      scores: {
        ...dataPillars?.people?.scores,
        ...integrityPillars.people?.scores,
      },
    },
  };
};

export const createAssessmentReportSectionUpdatePayload = (
  section: AssessmentReportSectionId,
  values: EditSolutionFormValues,
): SolutionUpdatePayload => {
  switch (section) {
    case "text":
      return {
        shortDescription: values["solution-short-description"] || null,
        assessmentReport: {
          sdgInfo: (values.assessmentReport.sdgInfo ?? "").trim(),
          developerInfo: (values.assessmentReport.developerInfo ?? "").trim(),
          monitoringInfo: (values.assessmentReport.monitoringInfo ?? "").trim(),
          locationInfo: (values.assessmentReport.locationInfo ?? "").trim(),
        },
      };
    case "images":
      return {
        locationPinMapLink: values["location-pin-map-link"] || null,
        satelliteImagery: normaliseSatelliteImageryForUpdate(
          values["satellite-imagery"],
        ),
      };
    case "data":
      return {
        pillars: createAssessmentDataPillarsUpdate(values),
        SDG: values["solution-sdg"] || [],
        assessmentReport: {
          benefits: normaliseUniqueBenefits(values.assessmentReport.benefits),
        },
      };
  }
};
